/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import type { IconProps } from 'labstep-web/core/Icon/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { getProtocolValueProps } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/utils';
import { ResourceItemCellRenderer } from 'labstep-web/grid/SmartTableProtocolValue/components/CellRenderer/ResourceItemCellRenderer';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import { AmountUpdate } from 'labstep-web/models/amount-update.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { evaluateProtocolValue } from 'labstep-web/services/protocol-value.service';

export const getProtocolValueResourceItemColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
): SmartTableColDef => ({
  colId: `resource_item`,
  sortable: true,
  headerName: 'Item',
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: (params: HeaderComponentParamsProps) => {
    return {
      toolPanelColumnName: `Item`,
      icon: ICONS.resource_item.primary,
      experimentOrProtocol,
    };
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolValueService.getEditable(params),
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }

    return params.data?.resourceItem?.guid;
  },
  cellRenderer: (params: CellRendererParams<ProtocolValue>) => {
    if (!params.node) {
      return null;
    }

    const protocolValue = params.data as ProtocolValue;
    if (experimentOrProtocol instanceof Protocol) {
      return protocolValue && protocolValue.is_input ? (
        <span>
          <DataGridPlaceholder
            children={'To be selected'}
            params={params}
            editable={GridSmartTableProtocolValueService.getEditable(
              params,
            )}
          />
        </span>
      ) : (
        <span>
          <DataGridPlaceholder
            children={'To be created'}
            params={params}
            editable={GridSmartTableProtocolValueService.getEditable(
              params,
            )}
          />
        </span>
      );
    }

    const { canUpdate, content, iconName, iconColor } =
      evaluateProtocolValue(protocolValue);
    const actionIconProps: IconProps = {
      name: iconName,
      color: iconColor,
      popup: {
        content: content,
        size: 'small',
      },
      onClick: canUpdate
        ? () => {
            return GridSmartTableProtocolValueService.createEntity(
              params,
              AmountUpdate.entityName,
              {
                protocol_value_id: protocolValue.guid,
              },
            );
          }
        : undefined,
    };

    const protocolValueResourceItem = protocolValue?.is_input
      ? protocolValue.resourceItem
      : protocolValue.resource_item_output;

    if (protocolValueResourceItem) {
      return (
        <ResourceItemCellRenderer
          protocolValue={protocolValue}
          resourceItemId={protocolValueResourceItem.id!}
          actionIconProps={actionIconProps}
        />
      );
    }

    return protocolValue?.is_input ? (
      <span>
        <DataGridPlaceholder
          children={'Select'}
          params={params}
          editable={GridSmartTableProtocolValueService.getEditable(
            params,
          )}
        />
      </span>
    ) : (
      <DataGridPlaceholder
        children={'Create'}
        params={params}
        editable={GridSmartTableProtocolValueService.getEditable(
          params,
        )}
      />
    );
  },
  cellEditor: (params: any) => {
    if (!params.node) {
      return null;
    }

    const protocolValue = params.data as ProtocolValue;

    const body: { [key: string]: any } = {
      [`${experimentOrProtocol.entityName}_id`]:
        experimentOrProtocol.guid,
      protocol_condition_id: params.node.data.id,
    };

    return null;
    // FIXME
    // return (
    //   <ResourceItemActionCreateProtocolValueWithProtocolValue
    //     modalProps={{
    //       initialModalState: true,
    //       onUnmount: () => {
    //         stopEditingAndFocus(params);
    //       },
    //     }}
    //     protocolValue={protocolValue}
    //     body={!protocolValue ? body : undefined}
    //     actionComponentProps={{
    //       disabled: false, // FIXME
    //       type: 'text',
    //       text: 'Create',
    //       elementProps: {
    //         placeholder: true,
    //         gridCell: true,
    //       },
    //     }}
    //     options={{
    //       onSuccess: () => {
    //         GridSmartTableProtocolValueService.refreshRow(params);
    //       },
    //     }}
    //   />
    // );
  },
  cellEditorParams: (params: CellEditorParams<ProtocolValue>) => {
    if (!params.node) {
      return false;
    }

    const protocolValue = params.data as ProtocolValue;

    const props = getProtocolValueProps(
      params.node.data,
      protocolValue || undefined,
    );

    props.onChange = (option): void => {
      if (protocolValue) {
        if (protocolValue.is_input) {
          GridSmartTableProtocolValueService.updateEntity(
            params,
            protocolValue.entityName,
            protocolValue.idAttr,
            {
              resource_item_id: option.id,
              [`${experimentOrProtocol.entityName}_id`]:
                experimentOrProtocol.guid,
            },
            () => {
              GridSmartTableProtocolValueService.refreshRow(params);
            },
          );
        } else {
          GridSmartTableProtocolValueService.updateEntity(
            params,
            protocolValue.entityName,
            protocolValue.idAttr,
            {
              protocol_value_origin_guid: protocolValue.guid,
            },
            () => {
              GridSmartTableProtocolValueService.refreshRow(params);
            },
          );
        }
      }
    };
    props.preventDefaultOnChange = true;
    return {
      props,
    };
  },
  valueSetter: (params) => {
    const newValue = params.newValue;
    if (
      !params.node ||
      newValue === '' ||
      params.newValue === params.oldValue
    ) {
      return false;
    }

    const protocolValue = params.data as ProtocolValue;

    if (protocolValue) {
      if (protocolValue.is_input) {
        if (params.newValue === null) {
          return GridSmartTableProtocolValueService.updateEntity(
            params,
            ProtocolValue.entityName,
            protocolValue.idAttr,
            {
              resource_item_id: null,
            },
            () => {
              GridSmartTableProtocolValueService.refreshRow(params);
            },
          );
        } else {
          return GridSmartTableProtocolValueService.updateEntity(
            params,
            ProtocolValue.entityName,
            protocolValue.idAttr,
            {
              resource_item_id: params.newValue,
              [`${experimentOrProtocol.entityName}_id`]:
                experimentOrProtocol.guid,
            },
            () => {
              GridSmartTableProtocolValueService.refreshRow(params);
            },
          );
        }
      } else if (params.newValue === null) {
        return GridSmartTableProtocolValueService.updateEntity(
          params,
          ResourceItem.entityName,
          protocolValue.resourceItem!.id,
          {
            protocol_value_origin_guid: null,
          },
          () => {
            GridSmartTableProtocolValueService.refreshRow(params);
          },
        );
      } else {
        return GridSmartTableProtocolValueService.updateEntity(
          params,
          ResourceItem.entityName,
          params.newValue,
          {
            protocol_value_origin_guid: protocolValue.guid,
          },
          () => {
            GridSmartTableProtocolValueService.refreshRow(params);
          },
        );
      }
    }

    return false;
  },
  // FIXME
  // cellStyle: (params) =>
  //   GridSmartTableProtocolValueService.getCellStyle(
  //     params,
  //     experimentOrProtocol,
  //     ProtocolValue.entityName,
  //     name,
  //   ),
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
  valueClearer: () => null,
});
